import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _classPrivateFieldLooseBase from "@babel/runtime/helpers/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/classPrivateFieldLooseKey";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _pick from "lodash/pick";
import _omit from "lodash/omit";
var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3, _bulkSavePanelIds, _bulkSavePanelsTimer;
import { action, computed, observable } from 'mobx';
import { LayoutMode } from '@kentik/ui-shared/boards/layout';
import { showErrorToast, showSuccessToast } from 'core/components';
import { Model } from 'core/model/Model';
import api from 'core/util/api';
import generateLayout, { updateLayout } from 'app/stores/boards/generateLayout';
import BoardPanelCollection from 'app/stores/boards/BoardPanelCollection';
import BoardLoadScheduler from 'app/stores/boards/BoardLoadScheduler';
import $users from 'app/stores/user/$users';
import isEqualLoose from 'app/util/isEqualsLoose';
import $auth from 'app/stores/$auth';
import $app from 'app/stores/$app';
import $boards from 'app/stores/boards/$boards';
import $dictionary from 'app/stores/$dictionary';
import $labels from 'app/stores/label/$labels';
import $setup from 'app/stores/$setup';
import $recentlyViewed from 'app/stores/recentlyViewed/$recentlyViewed';
import { Icons } from 'app/stores/library/constants';
import { BOARD_LABEL } from '@kentik/ui-shared/boards/boards';
import { RBACBoardPermission } from '@kentik/ui-shared/rbac/constants';
let BoardModel = (_dec = observable.ref, _dec2 = observable.ref, _class = (_bulkSavePanelIds = /*#__PURE__*/_classPrivateFieldLooseKey("bulkSavePanelIds"), _bulkSavePanelsTimer = /*#__PURE__*/_classPrivateFieldLooseKey("bulkSavePanelsTimer"), class BoardModel extends Model {
  constructor(...args) {
    super(...args);
    this.panelCollection = new BoardPanelCollection([], {
      parent: this
    });
    this.loadScheduler = new BoardLoadScheduler({
      shouldLoadAll: () => $app.isExport
    });
    this.isDuplicate = false;
    this.originalId = void 0;
    // Stores original state for dirty checks
    _initializerDefineProperty(this, "lastSaved", _descriptor, this);
    // store the chart refs for the panels
    _initializerDefineProperty(this, "chartRefs", _descriptor2, this);
    // Counts in-flight "silent" saves (e.g. debounced autosaves of
    // title/description). Surfaces via `isSilentSaving` so UI can suppress
    // the global Save spinner during these.
    _initializerDefineProperty(this, "silentSaveCount", _descriptor3, this);
    this.duplicate = (options = {}) => {
      const {
        save = true,
        deserialize = true
      } = options;
      const attributes = _omit(this.serialize(this.get()), ['id']);
      attributes.title = this.getUniqueCopyName('title');
      if (!this.collection) {
        throw new Error('Cannot duplicate a model that is not part of a collection');
      }
      const dupe = this.collection.build(attributes, {
        deserialize
      });
      dupe.isDuplicate = true;
      dupe.originalId = this.id;
      if (save) {
        dupe.save(attributes);
      }
      return dupe;
    };
    this.setTimeRange = time => {
      const config = this.get('config');
      this.safeSet({
        config: {
          ...config,
          time
        }
      });
      // Propagation to UDE panels is reactive: UdePanel reads board.config.time
      // and overlays it on the panel query via queryWithVariables (same pattern as board variables).
    };
    this.setTimezone = timezone => {
      const config = this.get('config');
      this.safeSet({
        config: {
          ...config,
          timezone
        }
      });
    };
    this.setLiveUpdate = updateFrequency => {
      // off-screen panels only re-run when back in view; stop their old streams now
      this.panelCollection.models.forEach(({
        queryModel
      }) => {
        if (queryModel && queryModel.query.updateFrequency !== updateFrequency) {
          queryModel.unsubscribe();
        }
      });
      const config = this.get('config');
      this.safeSet({
        config: {
          ...config,
          updateFrequency
        }
      });
      // Propagation to UDE panels is reactive: UdePanel reads board.config.updateFrequency
      // and overlays it on the panel query via queryWithVariables (same pattern as board variables).
    };
    Object.defineProperty(this, _bulkSavePanelIds, {
      writable: true,
      value: new Set()
    });
    Object.defineProperty(this, _bulkSavePanelsTimer, {
      writable: true,
      value: void 0
    });
  }
  get isSilentSaving() {
    return this.silentSaveCount > 0;
  }
  get urlRoot() {
    return '/api/ui/boards';
  }
  get defaults() {
    return {
      title: `Untitled ${BOARD_LABEL}`,
      type: null,
      draft: true,
      share_level: 'self',
      config: {
        isDashboard: true,
        variables: []
      }
    };
  }
  get messages() {
    const type = this.singlePanelView ? 'Chart' : this.typeLabel;
    return {
      create: `${type} "${this.name}" was added successfully`,
      update: `${type} "${this.name}" was updated successfully`,
      destroy: `${type} "${this.name}" was removed successfully`,
      duplicate: `${type} "${this.name}" was duplicated successfully`
    };
  }
  get type() {
    return 'Board';
  }
  get typeLabel() {
    return BOARD_LABEL;
  }
  get iconName() {
    return Icons.BOARD;
  }
  get labels() {
    return $labels.getLabels('board', this.id);
  }
  get labelIds() {
    return this.labels.map(label => label.id);
  }
  get selectOptionFormat() {
    return {
      value: String(this.id),
      label: this.name
    };
  }
  get name() {
    return this.singlePanelView ? this.panelCollection.models[0].title : this.get('title');
  }
  get slugFavorite() {
    return `board$${this.id}`;
  }
  get isFavorite() {
    return $setup.isFavorite(this.slugFavorite);
  }
  get isTrending() {
    return $recentlyViewed.isTrending(this);
  }
  get lastViewedDate() {
    return $recentlyViewed.getLastViewedDate(this);
  }
  get singlePanelView() {
    return !this.get('config').isDashboard && this.panelCollection.size === 1 && this.panelCollection.hasUdePanel;
  }

  // prevent layout updates while dragging
  get isDraggingPanel() {
    return this.panelCollection.models.some(panel => panel.isDragging);
  }
  get breakpoint() {
    return this.layoutMode === LayoutMode.LIST.id || $boards.isSmallScreen ? 'sm' : 'lg';
  }
  get layoutMode() {
    const {
      layoutMode
    } = this.get('config');
    if (layoutMode && layoutMode in LayoutMode) {
      return layoutMode;
    }
    return LayoutMode.DEFAULT.id;
  }

  // layout is stored on each panel

  get layout() {
    return generateLayout(this.panelCollection.topLevelPanels);
  }
  set layout(value) {
    const updatedPanels = updateLayout(this.panelCollection.topLevelPanels, value);
    this.bulkSavePanels(updatedPanels);
  }

  /**
   * This board or one of its panels has unsaved or draft changes
   */
  get isDirty() {
    if ((!this.isNew || this.panelCollection.size > 0) && (this.get('draft') || this.panelCollection.models.some(panel => panel.isDirty || panel.isLayoutDirty))) {
      return true;
    }
    const beforeData = this.lastSaved ?? {
      ...this.defaults,
      panels: []
    };
    const afterData = this.serialize();
    const fields = ['title', 'config'];
    const beforeFields = {
      ..._pick(beforeData, fields),
      panels: beforeData.panels?.map(panel => panel.id)
    };
    const afterFields = {
      ..._pick(afterData, fields),
      panels: afterData.panels?.map(panel => panel.id)
    };
    return !isEqualLoose(beforeFields, afterFields);
  }
  get userIsAuthor() {
    return Number(this.get('user_id')) === Number($auth.getActiveUserProperty('id', ''));
  }
  get author() {
    const {
      userIsAuthor,
      isPreset
    } = this;
    if (isPreset) {
      return 'Kentik';
    }
    if (userIsAuthor) {
      return 'Me';
    }
    const userId = this.get('user_id');
    if (!userId) {
      return '';
    }
    const authorModel = $users.collection.modelById[userId];
    return authorModel ? authorModel.get('user_full_name') : '';
  }
  get shareLevel() {
    const level = this.isPreset ? 'Preset' : 'Shared';
    return this.isUserLevel ? 'Private' : level;
  }
  get isPreset() {
    return this.get('share_level') === 'org' && `${this.get('company_id')}` === `${$dictionary.dictionary.templateDashboardsCID}`;
  }
  get isCompanyLevel() {
    return this.get('share_level') === 'org' && `${this.get('company_id')}` !== `${$dictionary.dictionary.templateDashboardsCID}`;
  }
  get isUserLevel() {
    return this.userIsAuthor && this.get('share_level') === 'self';
  }
  get navigatePath() {
    return this.singlePanelView ? `/v4/library/boards/${this.id}/panel/${this.panelCollection.models[0].id}` : `/v4/library/boards/${this.id}`;
  }
  get userCanEdit() {
    return !$app.isSubtenant && (this.userIsAuthor ||
    // Personal
    this.isCompanyLevel ||
    // Company (gated by RBAC board::update/delete in canEdit/canRemove)
    this.isPreset && $auth.isPresetCompany) // Preset
;
  }
  get canEdit() {
    if (this.isNew) {
      return $auth.hasRbacPermissions([RBACBoardPermission.CREATE]);
    }
    return this.userCanEdit && $auth.hasRbacLabelPermissions([RBACBoardPermission.UPDATE], this.labels);
  }
  get canRemove() {
    return this.userCanEdit && $auth.hasRbacLabelPermissions([RBACBoardPermission.DELETE], this.labels);
  }
  get lastEditedDate() {
    return this.get('edate') || this.get('cdate');
  }
  getVariable(variableName) {
    return this.get('config').variables?.find(v => v.name === variableName);
  }
  async addPanel({
    title = '',
    type,
    config = {},
    parent_panel_id = null,
    layout
  }) {
    const panel = this.panelCollection.forge({
      title,
      type,
      config,
      parent_panel_id,
      layout
    });
    this.panelCollection.add([panel]);
    const draft = type !== 'section'; // need to save sections in main table so sub-panels can reference it
    await panel.save({}, {
      draft,
      toast: false
    });
    return panel;
  }
  async removePanel(panel) {
    const subPanels = this.panelCollection.getSubPanels(panel.id);

    // Delete sub-panels first; if any fail, abort before touching the parent
    if (subPanels.length > 0) {
      await Promise.all(subPanels.map(p => p.draftDelete()));
    }
    await panel.draftDelete();
  }
  reset() {
    this.deserialize(this.lastSaved);
  }
  serialize(data) {
    const optimisticPanelIds = this.panelCollection.map(panel => `${panel.id}`.startsWith('i_') ? `${panel.id}` : panel.optimisticId);
    return super.serialize({
      ...(data || this.get()),
      panels: this.panelCollection.serialize(),
      optimisticPanelIds
    });
  }
  deserialize(data) {
    const {
      panels
    } = data || this.defaults;

    // This has the effect of avoiding a bunch of useless setup when simply fetching the parent collection
    // We only want this to exist when we fetch the model
    if (panels) {
      if (panels.length === this.panelCollection.size) {
        // board save, just update existing panel models
        panels.forEach((panelData, idx) => {
          const panel = this.panelCollection.at(idx);
          if (panel) {
            panel.safeSet(panel.deserialize(panelData));
          }
        });

        // If this board went from optimisticId to real id, we need it fixed here.
        this.panelCollection.hashModelsById();
      } else {
        this.panelCollection.set(panels);
      }
    }
    this.lastSaved = data;
    return data;
  }
  save(attributes = {}, options = {}) {
    const {
      draft = false,
      silent = false
    } = options;
    this.set('draft', draft && (this.isNew || this.get('draft')));
    if (draft || silent) {
      options.toast = false;
    }
    if (this.isDuplicate) {
      const url = `${this.urlRoot}/duplicate`;
      const label = 'creating';
      this.requestStatus = label;
      return api.post(url, {
        data: {
          id: this.originalId,
          title: this.get('title')
        }
      }).then(attrs => {
        showSuccessToast(`Successfully cloned ${this.typeLabel}.`);
        this.isDuplicate = false;
        this.requestStatus = null;
        this.set(this.deserialize(attrs));
        if (this.collection) {
          this.collection.add([this]);
        }
        return attrs;
      }, error => {
        showErrorToast(`Failed to clone ${this.typeLabel}.`);
        this.error = {
          label,
          body: error
        };
        this.requestStatus = null;
        throw error;
      });
    }
    if (silent) {
      this.silentSaveCount += 1;
    }
    const savePromise = super.save(attributes, options).then(attrs => $labels.labels.fetch({
      force: true
    }).then(() => attrs));
    if (silent) {
      const decrement = action(() => {
        this.silentSaveCount = Math.max(0, this.silentSaveCount - 1);
      });
      savePromise.then(decrement, decrement);
    }
    return savePromise;
  }
  bulkSavePanels(panels) {
    panels.forEach(panel => {
      if (!panel.isNew) {
        _classPrivateFieldLooseBase(this, _bulkSavePanelIds)[_bulkSavePanelIds].add(panel.id);
      }
    });
    if (_classPrivateFieldLooseBase(this, _bulkSavePanelIds)[_bulkSavePanelIds].size === 0) {
      return;
    }
    if (this.isDraggingPanel) {
      // wait until dragging is done
      return;
    }
    clearTimeout(_classPrivateFieldLooseBase(this, _bulkSavePanelsTimer)[_bulkSavePanelsTimer]);
    _classPrivateFieldLooseBase(this, _bulkSavePanelsTimer)[_bulkSavePanelsTimer] = setTimeout(() => {
      const savePanels = Array.from(_classPrivateFieldLooseBase(this, _bulkSavePanelIds)[_bulkSavePanelIds]).map(id => {
        const panel = this.panelCollection.get(id);
        const isDirty = panel?.isDirty || panel?.isLayoutDirty;
        // make sure panel still has updates
        return isDirty ? panel.serialize() : null;
      }).filter(Boolean);
      _classPrivateFieldLooseBase(this, _bulkSavePanelIds)[_bulkSavePanelIds].clear();
      if (savePanels.length > 0) {
        api.put(`${this.url}/panels`, {
          data: {
            panels: savePanels,
            draft: true
          },
          showErrorToast: false
        }).catch(err => {
          console.error(`Unable to update panels: ${err}`);
        });
      }
    }, 300);
  }
  async discardDraftPanels() {
    this.setRequestStatus('destroying');
    const response = await api.put(`${this.url}/discardDraftPanels`).catch(() => null);
    this.setRequestStatus(null);
    if (response) {
      this.set(this.deserialize(response));
    }
  }
  refreshUdePanels() {
    this.panelCollection.models.forEach(panel => {
      const {
        queryModel
      } = panel;
      if (!queryModel?.hasRun) {
        return;
      }
      const id = String(panel.id);
      if (this.loadScheduler.isRegistered(id)) {
        this.loadScheduler.schedule(id);
        return;
      }

      // unmounted (e.g. collapsed section): re-run when it mounts again
      queryModel.dirty = true;
    });
  }
  cleanupQueryModels() {
    this.loadScheduler.reset();
    this.panelCollection.models.forEach(panel => {
      panel.queryModel?.cleanup();
    });
  }
}), _descriptor = _applyDecoratedDescriptor(_class.prototype, "lastSaved", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {
      ...this.defaults,
      panels: []
    };
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "chartRefs", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "silentSaveCount", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _applyDecoratedDescriptor(_class.prototype, "labels", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "labels"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "labelIds", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "labelIds"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectOptionFormat", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectOptionFormat"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "name", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "name"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isFavorite", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isFavorite"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isTrending", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isTrending"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "lastViewedDate", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "lastViewedDate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "singlePanelView", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "singlePanelView"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "layoutMode", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "layoutMode"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "layout", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "layout"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isDirty", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isDirty"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "userIsAuthor", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "userIsAuthor"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "author", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "author"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "shareLevel", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "shareLevel"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isPreset", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isPreset"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isCompanyLevel", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isCompanyLevel"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "navigatePath", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "navigatePath"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "userCanEdit", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "userCanEdit"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canEdit", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canEdit"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canRemove", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canRemove"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "lastEditedDate", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "lastEditedDate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "deserialize", [action], Object.getOwnPropertyDescriptor(_class.prototype, "deserialize"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "save", [action], Object.getOwnPropertyDescriptor(_class.prototype, "save"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "refreshUdePanels", [action], Object.getOwnPropertyDescriptor(_class.prototype, "refreshUdePanels"), _class.prototype), _class);
export default BoardModel;